import { useState } from 'react'
import { useLocation } from 'react-router-dom'

const STORAGE_KEY = 'beacon_browse_search'
const MAX_ENTRIES = 20

// The router keys every document's first entry 'default', so a fresh load —
// typing the URL, a new tab session, a cold app start — would inherit whatever
// the tab last searched on its first entry. A reload or a back/forward load is
// the same entry and keeps it.
const FIRST_ENTRY_KEY = 'default'
let firstEntryChecked = false

function dropStaleFirstEntry() {
  if (firstEntryChecked) return
  firstEntryChecked = true
  const nav = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined
  if (nav?.type === 'navigate') writeSaved(FIRST_ENTRY_KEY, undefined)
}

// Query per history entry (`location.key`), so tapping a result and swiping
// back lands on the same search, while a fresh visit — a new entry — starts clean.
function readSaved(entryKey: string): string | undefined {
  dropStaleFirstEntry()
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}')[entryKey]
  } catch {
    return undefined
  }
}

function writeSaved(entryKey: string, query: string | undefined) {
  try {
    const all: Record<string, string> = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}')
    delete all[entryKey]
    if (query !== undefined) all[entryKey] = query
    const kept = Object.entries(all).slice(-MAX_ENTRIES)
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(kept)))
  } catch {
    // Storage unavailable (private mode): search still works, just isn't restored.
  }
}

/**
 * The Search tab's query. Not router history state: every keystroke would be a
 * navigation, router updates run as transitions, and WebKit throws on frequent
 * `replaceState` — so the input stopped taking keystrokes whenever either
 * stalled (#505). sessionStorage writes are synchronous, so the saved query is
 * always current when a result is tapped.
 */
export function useSearchQuery() {
  const { key: entryKey } = useLocation()
  const [query, setQueryState] = useState(() => readSaved(entryKey) ?? '')

  // A new entry on the same page starts clean.
  const [prevEntryKey, setPrevEntryKey] = useState(entryKey)
  if (entryKey !== prevEntryKey) {
    setPrevEntryKey(entryKey)
    setQueryState(readSaved(entryKey) ?? '')
  }

  function setQuery(q: string) {
    setQueryState(q)
    writeSaved(entryKey, q || undefined)
  }

  return { query, setQuery }
}
